import {Box, Typography} from '@mui/material'
import classNames from 'classnames'
import React, {useEffect, useRef, useState, useCallback} from 'react'

import {useWheelColumnStyles} from './styles'
import {WheelColumnProps} from './types'

const ITEM_HEIGHT = 40
const VISIBLE_ROWS_DEFAULT = 5

export const WheelColumn: React.FC<WheelColumnProps> = ({
  values,
  selectedIndex,
  onSelectedIndex,
  visibleRows = VISIBLE_ROWS_DEFAULT,
  separator,
  testID
}) => {
  const classes = useWheelColumnStyles()
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startY, setStartY] = useState(0)
  const [currentTranslateY, setCurrentTranslateY] = useState(0)

  // Calculate the center offset to show selected item in the middle
  const centerOffset = Math.floor(visibleRows / 2) * ITEM_HEIGHT

  // Calculate transform position based on selected index
  const getTransformY = useCallback(
    (index: number) => {
      return centerOffset - index * ITEM_HEIGHT
    },
    [centerOffset]
  )

  // Update transform when selectedIndex changes
  useEffect(() => {
    setCurrentTranslateY(getTransformY(selectedIndex))
  }, [selectedIndex, getTransformY])

  // Handle click selection
  const handleItemClick = (index: number) => {
    if (index !== selectedIndex) {
      onSelectedIndex(index)
    }
  }

  // Handle mouse wheel scrolling
  const handleWheel = (e: React.WheelEvent) => {
    const direction = e.deltaY > 0 ? 1 : -1
    const newIndex = Math.max(0, Math.min(values.length - 1, selectedIndex + direction))
    if (newIndex !== selectedIndex) {
      onSelectedIndex(newIndex)
    }
  }

  // Handle touch/mouse drag
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setStartY(e.clientY)
  }

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return

      const deltaY = e.clientY - startY
      const newTranslateY = getTransformY(selectedIndex) + deltaY
      setCurrentTranslateY(newTranslateY)
    },
    [isDragging, startY, selectedIndex, getTransformY]
  )

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return

    setIsDragging(false)

    // Calculate the new index based on the final position
    const deltaFromCenter = currentTranslateY - centerOffset
    const newIndex = Math.round(-deltaFromCenter / ITEM_HEIGHT)
    const clampedIndex = Math.max(0, Math.min(values.length - 1, newIndex))

    if (clampedIndex !== selectedIndex) {
      onSelectedIndex(clampedIndex)
    } else {
      // Snap back to correct position
      setCurrentTranslateY(getTransformY(selectedIndex))
    }
  }, [
    isDragging,
    currentTranslateY,
    centerOffset,
    selectedIndex,
    values.length,
    onSelectedIndex,
    getTransformY
  ])

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  return (
    <>
      <Box
        ref={containerRef}
        className={classes.columnContainer}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        data-testid={testID}
      >
        <Box className={classes.scrollContainer}>
          {/* Selection overlay */}
          <Box className={classes.selectionOverlay} />

          {/* Items list */}
          <ul
            className={classes.itemsList}
            style={{
              transform: `translateY(${currentTranslateY}px)`,
              transition: isDragging ? 'none' : 'transform 0.3s ease-out'
            }}
          >
            {values.map((value, index) => (
              <li
                key={`${value}-${index}`}
                className={classNames(classes.item, {
                  [classes.selectedItem]: index === selectedIndex
                })}
                style={{height: ITEM_HEIGHT}}
              >
                <Box
                  onClick={() => handleItemClick(index)}
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <Typography variant="body1" component="span">
                    {value}
                  </Typography>
                </Box>
              </li>
            ))}
          </ul>
        </Box>
      </Box>

      {/* Separator */}
      {separator && (
        <Typography className={classes.separator} variant="h6" component="span">
          {separator}
        </Typography>
      )}
    </>
  )
}
